import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LockClosedIcon, LockOpenIcon } from "@heroicons/react/24/solid";
import * as Yup from "yup";
import {
  createProducts,
  updateProducts,
} from "../../features/admin/ProductSlice";
import { toast } from "react-toastify";
import { fetchCompanies } from "../../features/admin/CompanySlice";
import { fetchCategoryByTypePublic } from "../../features/admin/CategorySlice";
import { convertToSlug, formatCurrencyVND } from "../../utils/helps";
import { productStatus } from "../../utils/defaultValue";
import ImageManagerModal from "./ImageManagerModal";
import ApiCustom from "../../utils/ApiCustom";
import { PhotoIcon } from "@heroicons/react/24/outline";
import TextEditor from "../TextEditor";

const userId = localStorage.getItem("userId");
const lsCompanyId = localStorage.getItem("companyId");
const initialMakerCompanyId =
  lsCompanyId && lsCompanyId !== "null" ? Number(lsCompanyId) : null;

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

let userType = localStorage.getItem("type_user");
if (hasPermission("CONTENT_MANAGE")) {
  userType = "admin";
}

const productSchema = Yup.object().shape({
  name: Yup.string().trim().required("Tên sản phẩm không được để trống"),
  slug: Yup.string().trim().required("Slug không được để trống"),
  price: Yup.mixed()
    .test(
      "price-required-by-contact",
      "Giá tiền không được để trống",
      function (value) {
        const { isContactPrice } = this.parent;
        if (isContactPrice === true) return true;
        return value !== "" && value !== null && value !== undefined;
      },
    )
    .test("isNotZero", "Giá tiền phải lớn hơn 0", function (value) {
      const { isContactPrice } = this.parent;
      if (isContactPrice === true) return true;
      return String(value) !== "0";
    }),
  quantity: Yup.string().required("Số lượng không được để trống"),
  priority: Yup.number()
    .transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return 0;
      }
      return Number.isNaN(value) ? 0 : value;
    })
    .default(0),
  categoryId: Yup.number()
    .typeError("Danh mục không hợp lệ")
    .positive("Danh mục không hợp lệ")
    .required("Danh mục không được để trống"),
  madeBy: Yup.string().trim(),
  status: Yup.string().trim(),
  description: Yup.string().trim(),
  unit: Yup.string().when("isContactPrice", {
    is: true,
    then: (schema) => schema.trim().nullable(),
    otherwise: (schema) => schema.trim().required("Đơn vị không được để trống"),
  }),
});

const fieldLabels = {
  productCode: "Mã sản phẩm",
  name: "Tên sản phẩm",
  description: "Mô tả",
  price: "Giá bán",
  discountPrice: "Giá bán đã trừ khuyến mãi",
  quantity: "Số lượng",
  status: "Trạng thái",
  companyId: "Công ty",
  companyURL: "Link công ty",
  slug: "Slug sản phẩm",
  urlImage: "Hình ảnh sản phẩm",
  madeBy: "Được thiết kế bởi",
  priority: "Ưu tiên",
  categoryId: "Danh mục",
  unit: "Đơn vị tính",
  gift: "Quà tặng",
  giftCondition: "Số tiền nhận quà",
};

const ProductModal = ({ productSlug, company, onClose }) => {
  const [product, setProduct] = useState(null);

  const [makerCompanyId, setMakerCompanyId] = useState(initialMakerCompanyId);
  const hasMakerCompany = !!makerCompanyId;

  const [myCompanyName, setMyCompanyName] = useState("Đơn vị của tôi");

  const [formData, setFormData] = useState({
    productCode: "",
    name: "",
    description: "",
    price: "",
    isContactPrice: false,
    discountPrice: "",
    quantity: 1,
    status: "available",
    companyId: 0,
    companyURL: "",
    slug: "",
    urlImage: "",
    madeBy: hasMakerCompany ? "KATEC" : "OTHER",
    priority: 0,
    categoryId: 0,
    gift: "",
    giftCondition: "",
    unit: "",
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState("");
  const [hasNewImage, setHasNewImage] = useState(false);
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.categories);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locked, setLocked] = useState(false);

  const isDisabled = useMemo(() => !!(product && locked), [product, locked]);

  const canShowPriority =
    (userType == "admin" && hasPermission("PRODUCT_APPROVE")) || userId == 1;

  const getProductDetail = () => {
    ApiCustom.get(`/product-show/${productSlug}`)
      .then((res) => {
        const data = res.data?.data || null;
        setProduct(data);

        if (data) {
          if (data.companyId) setMakerCompanyId(Number(data.companyId));

          const imageValue = Array.isArray(data.urlImage)
            ? data.urlImage[0] || ""
            : data.urlImage || "";

          const next = {
            productCode: data.productCode ?? "",
            name: data.name ?? "",
            description: data.description ?? "",
            price: data.price ?? "",
            isContactPrice: Boolean(data.isContactPrice),
            discountPrice: data.discountPrice ?? "",
            quantity: data.quantity ?? 1,
            status: data.status ?? "available",
            companyId: data.companyId ?? 0,
            companyURL: data.companyURL ?? "",
            slug: data.slug ?? "",
            urlImage: imageValue,
            madeBy: data.madeBy ?? (data.companyId ? "KATEC" : "OTHER"),
            priority: data.priority ?? 0,
            categoryId: data.categoryId ?? 0,
            gift: data.gift ?? "",
            giftCondition: data.giftCondition ?? "",
            unit: data.unit ?? "",
          };

          if (!data.companyId && next.madeBy === "KATEC") next.madeBy = "OTHER";

          setFormData(next);
          setHasNewImage(true);
          setImagePreview(imageValue);
          setLocked(!!data.locked);
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (productSlug) getProductDetail();
    dispatch(fetchCompanies());
    dispatch(fetchCategoryByTypePublic());
  }, []);

  useEffect(() => {
    if (!makerCompanyId) return;
    ApiCustom.get(`/company-show-by-id/${makerCompanyId}`)
      .then((res) => {
        const name = res?.data?.data?.companyName;
        setMyCompanyName(name || "Đơn vị của tôi");
      })
      .catch(() => setMyCompanyName("Đơn vị của tôi"));
  }, [makerCompanyId]);

  const getImageUrl = () => {
    if (hasNewImage) return imagePreview;
    if (product && product.urlImage) {
      return Array.isArray(product.urlImage)
        ? product.urlImage[0] || ""
        : product.urlImage;
    }
    return "";
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    let rawValue = value;

    if (
      [
        "price",
        "discountPrice",
        "priority",
        "giftCondition",
        "quantity",
      ].includes(name)
    ) {
      rawValue = value.replace(/[^0-9]/g, "");
    }

    const parsedValue = rawValue === "" ? "" : Number(rawValue);

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));

    try {
      await Yup.reach(productSchema, name).validate(rawValue, {
        context: formData,
        parent: {
          ...formData,
          [name]: parsedValue,
        },
      });
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (validationError) {
      setErrors((prev) => ({ ...prev, [name]: validationError.message }));
    }

    if (["price", "discountPrice", "giftCondition"].includes(name)) {
      setTimeout(() => {
        const inputElement = e.target;
        const formattedValue = formatCurrencyVND(Number(rawValue));
        const position = formattedValue.length - 2;
        inputElement.setSelectionRange(position, position);
      }, 0);
    }
  };

  const normalizeNullable = (fd) => ({
    ...fd,
    isContactPrice: Boolean(fd.isContactPrice),
    discountPrice: fd.discountPrice === "" ? null : fd.discountPrice,
    giftCondition: fd.giftCondition === "" ? null : fd.giftCondition,
    priority:
      fd.priority === "" || fd.priority === null || fd.priority === undefined
        ? 0
        : Number(fd.priority),
    unit: fd.isContactPrice === true ? "" : fd.unit,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let updatedFormData;

      if (product) {
        updatedFormData = normalizeNullable({
          ...formData,
          isContactPrice: Boolean(formData.isContactPrice),
          price: formData.isContactPrice === true ? 0 : formData.price,
          companyId: product.companyId,
          companyURL: product.companyURL,
        });
      } else {
        if (userId == 1) {
          updatedFormData = normalizeNullable({
            ...formData,
            isContactPrice: Boolean(formData.isContactPrice),
            price: formData.isContactPrice === true ? 0 : formData.price,
          });
        } else {
          updatedFormData = normalizeNullable({
            ...formData,
            isContactPrice: Boolean(formData.isContactPrice),
            price: formData.isContactPrice === true ? 0 : formData.price,
            companyId: company?.id,
            companyURL: company?.websiteOrHotline,
          });
        }
      }

      if (updatedFormData.urlImage == "") {
        toast.error("Vui lòng chọn hình ảnh");
        return;
      }

      await productSchema.validate(updatedFormData, { abortEarly: false });

      if (product) {
        await dispatch(
          updateProducts({ id: product.id, newPost: updatedFormData }),
        ).unwrap();
        toast.success("Cập nhật thành công!");
        onClose();
      } else {
        await dispatch(createProducts(updatedFormData)).unwrap();
        toast.success("Thêm sản phẩm mới thành công!");
        onClose();
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
        console.error("Lỗi khi lưu sản phẩm:", error);
      }
    }
  };

  const handleChangeDescription = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    const files = e.target?.files;
    let newValue = name === "images" && files ? files[0] : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleToggleLock = async () => {
    if (!product) return;
    try {
      await ApiCustom.put(`/product-change-locked/${product.id}`, {
        locked: !locked,
      });
      setLocked((prev) => !prev);
      setProduct((prev) => (prev ? { ...prev, locked: !prev.locked } : prev));
      toast.success(!locked ? "Đã khoá!" : "Đã mở khoá!");
    } catch (e) {
      toast.error("Có lỗi khi đổi trạng thái khoá!");
    }
  };

  const renderGiftFields = () => (
    <div className="grid grid-cols-2 gap-4 items-start">
      <div>
        <label className="block text-sm font-medium mb-1">
          {fieldLabels.gift}
        </label>
        <input
          type="text"
          name="gift"
          value={formData.gift}
          disabled={isDisabled}
          onChange={(e) => {
            setFormData({ ...formData, gift: e.target.value });
          }}
          className={`w-full p-2 border rounded-md ${
            errors.gift ? "border-red-500" : "border-gray-300"
          } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          {fieldLabels.giftCondition}
        </label>
        <input
          type="text"
          name="giftCondition"
          value={formatCurrencyVND(formData.giftCondition)}
          disabled={isDisabled}
          onChange={(e) => {
            const rawValue = e.target.value.replace(/[^0-9]/g, "");
            setFormData((prev) => ({
              ...prev,
              giftCondition: rawValue === "" ? "" : Number(rawValue),
            }));
            setTimeout(() => {
              const inputElement = e.target;
              const formattedValue = formatCurrencyVND(Number(rawValue));
              const position = formattedValue.length - 2;
              inputElement.setSelectionRange(position, position);
            }, 0);
          }}
          className={`w-full p-2 border rounded-md ${
            errors.giftCondition ? "border-red-500" : "border-gray-300"
          } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        />
      </div>
    </div>
  );

  const renderPriorityField = () => (
    <div>
      <label className="block text-sm font-medium mb-1">
        {fieldLabels.priority}
      </label>
      <input
        disabled={isDisabled || userId !== "1"}
        type="text"
        name="priority"
        value={formData.priority}
        onChange={handleChange}
        className={`w-full p-2 border rounded-md ${
          errors.priority ? "border-red-500" : "border-gray-300"
        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
      />
      {errors.priority && (
        <p className="mt-1 text-sm text-red-600">{errors.priority}</p>
      )}
    </div>
  );

  const renderUnitField = () => (
    <div>
      <label className="block text-sm font-medium mb-1">
        {fieldLabels.unit}
        <i className="text-red-600"> *</i>
      </label>
      <input
        type="text"
        name="unit"
        value={formData.unit}
        disabled={isDisabled}
        onChange={(e) => {
          setFormData({ ...formData, unit: e.target.value });
          setErrors({ ...errors, unit: "" });
        }}
        className={`w-full p-2 border rounded-md ${
          errors.unit ? "border-red-500" : "border-gray-300"
        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
      />
      {errors.unit && (
        <p className="mt-1 text-sm text-red-600">{errors.unit}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div>
          <div className="flex sticky top-0 bg-base-100 z-10 items-center p-6 justify-between">
            <h2 className="text-2xl font-bold">
              {product ? "Chỉnh sửa thông tin sản phẩm" : "Thêm sản phẩm mới"}
            </h2>
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            >
              X
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-start">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.productCode}
                  </label>
                  <input
                    type="text"
                    name="productCode"
                    value={formData.productCode || ""}
                    disabled={isDisabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        productCode: e.target.value,
                      })
                    }
                    className={`w-full p-2 border rounded-md ${
                      errors.productCode ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.categoryId}
                    <i className="text-red-600"> *</i>
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    disabled={isDisabled}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        categoryId: e.target.value,
                      });
                      setErrors({
                        ...errors,
                        categoryId: "",
                      });
                    }}
                    className={`w-full p-2 border min-h-11 rounded-md ${
                      errors.categoryId ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    <option value="">Chọn một tùy chọn</option>
                    {categories.map((item, idx) => (
                      <option key={idx} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {errors.categoryId && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.categoryId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.name}
                    <i className="text-red-600"> *</i>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    disabled={isDisabled}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: convertToSlug(e.target.value),
                      });
                      setErrors({ ...errors, name: "", slug: "" });
                    }}
                    className={`w-full p-2 border rounded-md ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.madeBy}
                  </label>
                  <select
                    name="madeBy"
                    value={formData.madeBy}
                    disabled={isDisabled}
                    onChange={(e) => {
                      setFormData({ ...formData, madeBy: e.target.value });
                    }}
                    className={`w-full p-2 border min-h-10 rounded-md ${
                      errors.madeBy ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    {hasMakerCompany ? (
                      <>
                        <option value="KATEC">{myCompanyName}</option>
                        <option value="OTHER">Công ty khác</option>
                      </>
                    ) : (
                      <option value="OTHER">Công ty khác</option>
                    )}
                  </select>
                  {errors.madeBy && (
                    <p className="mt-1 text-sm text-red-600">{errors.madeBy}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.slug}
                    <i className="text-red-600"> *</i>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    disabled={isDisabled}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    className={`w-full p-2 border rounded-md ${
                      errors.slug ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.slug && (
                    <p className="mt-1 text-sm text-red-600">{errors.slug}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.status}
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    disabled={isDisabled}
                    onChange={(e) => {
                      setFormData({ ...formData, status: e.target.value });
                    }}
                    className={`w-full min-h-11 p-2 border rounded-md ${
                      errors.status ? "border-red-500" : "border-gray-300"
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    {productStatus.map((item, idx) => (
                      <option key={idx} value={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                  )}
                </div>

                <div>
                  <div className="min-h-11 flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isContactPrice === true}
                      disabled={isDisabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          isContactPrice: checked,
                          price: checked ? 0 : prev.price,
                          discountPrice: checked ? "" : prev.discountPrice,
                          unit: checked ? "" : prev.unit,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          price: "",
                          discountPrice: "",
                          unit: "",
                        }));
                      }}
                      className="checkbox checkbox-info"
                    />
                    <label className="block text-sm font-medium mb-1 ml-4">
                      Giá liên hệ
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Lưu ý: Tuỳ chọn khi sản phẩm áp dụng hình thức báo giá trực
                    tiếp cho khách hàng.
                  </p>
                </div>

                <div>{renderGiftFields()}</div>

                <div>
                  {formData.isContactPrice !== true ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {fieldLabels.price}
                          <i className="text-red-600"> *</i>
                        </label>
                        <input
                          disabled={isDisabled}
                          type="text"
                          name="price"
                          value={formatCurrencyVND(formData.price)}
                          onChange={handleChange}
                          className={`w-full p-2 border rounded-md ${
                            errors.price ? "border-red-500" : "border-gray-300"
                          } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                        />
                        {errors.price && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.price}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {fieldLabels.discountPrice}
                        </label>
                        <input
                          disabled={isDisabled}
                          type="text"
                          name="discountPrice"
                          value={formatCurrencyVND(formData.discountPrice)}
                          onChange={handleChange}
                          className="w-full p-2 border rounded-md border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ) : canShowPriority ? (
                    renderPriorityField()
                  ) : (
                    <div className="min-h-11" />
                  )}
                </div>

                {formData.isContactPrice === true ? (
                  canShowPriority ? (
                    <div className="min-h-11" />
                  ) : (
                    <div className="min-h-11" />
                  )
                ) : (
                  <div>{renderUnitField()}</div>
                )}

                {canShowPriority && formData.isContactPrice !== true ? (
                  <>
                    <div>{renderPriorityField()}</div>
                    <div />
                  </>
                ) : null}
              </div>

              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Ảnh sản phẩm <span className="text-red-500"> *</span>
                    </label>
                    <div className="mt-1 flex items-center space-x-4">
                      <div className="w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                        {getImageUrl() ? (
                          <img
                            src={getImageUrl()}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <PhotoIcon className="w-10 h-10 text-gray-300" />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col space-y-2">
                        <div
                          className={`cursor-pointer py-2 px-4 border rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 ${
                            isDisabled
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "text-gray-700"
                          }`}
                          onClick={
                            isDisabled ? undefined : () => setIsModalOpen(true)
                          }
                          tabIndex={isDisabled ? -1 : 0}
                          aria-disabled={isDisabled}
                        >
                          {product ? "Cập nhật ảnh" : "Chọn ảnh"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    {fieldLabels.description}
                  </label>
                  <TextEditor
                    value={formData.description}
                    name="description"
                    onChange={handleChangeDescription}
                    importCustomInsertImage={true}
                    canUpdate={!isDisabled}
                  />
                </div>
              </div>
            </div>

            <div
              className={`flex sticky bottom-0 bg-base-100 z-10 p-6 pt-0 pb-4 ${
                product ? "justify-between" : "justify-end"
              } border-t`}
            >
              <div className="pt-4">
                {product && hasPermission("PRODUCT_APPROVE") && (
                  <button
                    type="button"
                    onClick={handleToggleLock}
                    className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium flex items-center space-x-2 btn-warning hover:bg-yellow-500"
                  >
                    {locked ? (
                      <>
                        <LockClosedIcon className="h-5 w-5" />
                        <span>Đã khoá thông tin</span>
                      </>
                    ) : (
                      <>
                        <LockOpenIcon className="h-5 w-5" />
                        <span>Khoá thông tin</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {isDisabled ? (
                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Đóng
                  </button>
                </div>
              ) : (
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Huỷ
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {product ? "Cập nhật" : "Thêm mới"}
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {isModalOpen && (
        <ImageManagerModal
          onClose={() => setIsModalOpen(false)}
          onPress={(media) => {
            setIsModalOpen(false);
            setHasNewImage(true);
            setImagePreview(media.url);
            setFormData((prev) => ({ ...prev, urlImage: media.url }));
          }}
          multiImage={true}
          selectedMultiMedia={[]}
          deActiveInsertImage={true}
        />
      )}
    </div>
  );
};

export default ProductModal;
